import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { motion, useInView, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import Monitor from './components/Monitor';

// Core Values Monitor Animations
const HumanFirstInnovationAnimation = () => {
  return (
    <Monitor width="w-48" height="h-32">
      <div className="w-full h-full bg-slate-900 p-3 flex flex-col justify-center items-center gap-3">
        {/* AI Processing Visualization */}
        <div className="flex items-center justify-center gap-2">
          <motion.div
            className="w-3 h-3 bg-purple-500 rounded-full"
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.div
            className="w-3 h-3 bg-blue-500 rounded-full"
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
          />
          <motion.div
            className="w-3 h-3 bg-indigo-500 rounded-full"
            animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
          />
        </div>
        <div className="text-center">
          <div className="text-white text-[10px] font-medium mb-1">AI Processing</div>
          <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full" />
        </div>
      </div>
    </Monitor>
  );
};

const CollaborativeResearchAnimation = () => {
  return (
    <Monitor width="w-48" height="h-32">
      <div className="w-full h-full bg-slate-900 p-3 flex flex-col justify-center items-center">
        {/* Network Visualization */}
        <div className="relative w-20 h-20">
          {/* Central node */}
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 3, repeat: Infinity }}
          />
          {/* Connecting nodes */}
          {[
            { x: 10, y: 10, delay: 0 },
            { x: 30, y: 10, delay: 0.5 },
            { x: 10, y: 30, delay: 1 },
            { x: 30, y: 30, delay: 1.5 }
          ].map((node, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-slate-600 rounded-full"
              style={{ left: node.x, top: node.y }}
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: node.delay }}
            />
          ))}
        </div>
        <div className="text-center mt-2">
          <div className="text-white text-[10px] font-medium">Research Network</div>
        </div>
      </div>
    </Monitor>
  );
};

const GlobalImpactAnimation = () => {
  return (
    <Monitor width="w-48" height="h-32">
      <div className="w-full h-full bg-slate-900 p-3 flex flex-col justify-center items-center">
        {/* Global Reach Indicators */}
        <div className="flex justify-center gap-1 mb-2">
          {['🇺🇸', '🇪🇺', '🇦🇺', '🇯🇵', '🇮🇳'].map((flag, i) => (
            <motion.div
              key={i}
              className="text-sm"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
            >
              {flag}
            </motion.div>
          ))}
        </div>
        <div className="text-center">
          <div className="text-white text-[10px] font-medium mb-1">Global Reach</div>
          <div className="flex justify-center gap-1">
            <motion.div
              className="w-2 h-2 bg-orange-500 rounded-full"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
            <motion.div
              className="w-2 h-2 bg-orange-500 rounded-full"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            />
            <motion.div
              className="w-2 h-2 bg-orange-500 rounded-full"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
            />
          </div>
        </div>
      </div>
    </Monitor>
  );
};

const UserCentricDesignAnimation = () => {
  return (
    <Monitor width="w-48" height="h-32">
      <div className="w-full h-full bg-slate-900 p-3 flex flex-col justify-center items-center">
        {/* Design Interface Elements */}
        <div className="grid grid-cols-2 gap-1 mb-2">
          <motion.div
            className="w-6 h-4 bg-slate-700 rounded border border-slate-600 flex items-center justify-center"
            animate={{ borderColor: ['rgb(71 85 105)', 'rgb(139 92 246)', 'rgb(71 85 105)'] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <div className="w-1 h-1 bg-slate-400 rounded-full" />
          </motion.div>
          <motion.div
            className="w-6 h-4 bg-slate-800 rounded border border-slate-600"
            animate={{ backgroundColor: ['rgb(30 41 59)', 'rgb(139 92 246)', 'rgb(30 41 59)'] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="w-6 h-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <motion.div
            className="w-6 h-4 bg-slate-700 rounded border border-slate-600 flex items-center justify-center"
            animate={{ rotate: [0, 5, 0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <div className="w-1.5 h-1.5 border border-slate-400 rounded" />
          </motion.div>
        </div>
        <div className="text-center">
          <div className="text-white text-[10px] font-medium">Design Studio</div>
        </div>
      </div>
    </Monitor>
  );
};

const About = () => {
  const { isMobile } = useIsMobile();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [1, 1, 1, 0]);

  // Section refs
  const headerRef = useRef(null);
  const missionRef = useRef(null);
  const valuesRef = useRef(null);
  const storyRef = useRef(null);

  // InView hooks
  const headerInView = useInView(headerRef, { amount: 0.3, once: true });
  const missionInView = useInView(missionRef, { amount: 0.2, once: true });
  const valuesInView = useInView(valuesRef, { amount: 0.2, once: true });
  const storyInView = useInView(storyRef, { amount: 0.2, once: true });

  const values = [
    {
      title: "Human-First Innovation",
      description: "We believe AI should amplify human intelligence, not replace it. Our tools are designed to enhance researcher capabilities.",
      Animation: HumanFirstInnovationAnimation
    },
    {
      title: "Collaborative Research",
      description: "The best discoveries happen when brilliant minds work together. We build tools that make collaboration seamless.",
      Animation: CollaborativeResearchAnimation
    },
    {
      title: "Global Impact",
      description: "Research has the power to solve humanity's greatest challenges. We're democratizing access to advanced research tools.",
      Animation: GlobalImpactAnimation
    },
    {
      title: "User-Centric Design",
      description: "Every feature is designed with researchers in mind. We listen to our community and build what they actually need.",
      Animation: UserCentricDesignAnimation
    }
  ];

     return (
     <div ref={containerRef} className="landing-gradient-bg overflow-x-hidden">

       {/* Animated Grid Background */}

             {/* Hero Section */}
       <motion.div
         ref={headerRef}
         style={{ y, opacity }}
         className={`relative landing-hero ${isMobile ? 'pt-16 pb-8 px-4' : 'pt-32 pb-20 px-8 md:px-16 xl:px-32 2xl:px-40'} overflow-hidden`}
       >
         <div className="max-w-screen-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={headerInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="mb-8"
          >

            <h1 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-5xl md:text-6xl leading-snug' : 'text-5xl md:text-7xl leading-tight'}`}>
              <span className="text-slate-900 dark:text-white">About</span>
              <br />
              <span className="text-slate-900 dark:text-white">Synkro</span>
            </h1>
            {!isMobile && (
              <p className="text-xl text-black dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
                We're on a mission to revolutionize how research is conducted, making it more efficient, 
                collaborative, and accessible to researchers worldwide.
              </p>
            )}
          </motion.div>
        </div>
      </motion.div>

      {/* Mission Section */}
      <motion.div
        ref={missionRef}
        className={`relative z-10 landing-section ${isMobile ? 'px-4 py-8' : 'px-8 md:px-16 xl:px-32 2xl:px-40 py-24'}`}
      >
         <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={missionInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-blue-600/5 to-purple-600/10 rounded-3xl blur-3xl" />
            <div className={`relative ${isMobile ? 'space-y-8' : 'grid grid-cols-2 gap-16 items-center'}`}>
              <div className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={missionInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="flex items-center gap-4"
                >
                  <div>
                    <h2 className={`${isMobile ? 'text-3xl' : 'text-5xl'} font-bold text-slate-900 dark:text-white`}>
                      Our Mission
                    </h2>
                  </div>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={missionInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 leading-relaxed`}
                >
                  Research is the engine of human progress, but traditional research workflows are fragmented,
                  time-consuming, and often isolating. We're changing that.
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={missionInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6, duration: 0.6 }}
                  className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 leading-relaxed`}
                >
                  Synkro combines the power of artificial intelligence with intuitive design to create
                  a unified platform where researchers can ideate, organize, analyze, and collaborate
                  more effectively than ever before.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={missionInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8, duration: 0.6 }}
                  className="flex flex-col sm:flex-row gap-4 pt-4"
                >
                  <Link to="/auth">
                    <Button
                      size="lg"
                      className="relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                      <span className="relative z-10 font-medium flex items-center">
                        Join Our Mission
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </span>
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="ghost"
                      size="lg"
                      className="relative bg-black/5 dark:bg-white/5 backdrop-blur-md hover:bg-black/10 dark:hover:bg-white/10 border border-black/20 dark:border-white/20 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                      <span className="relative z-10 font-medium">
                        Get in Touch
                      </span>
                    </Button>
                  </Link>
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={missionInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-purple-700 to-blue-700 p-8 md:p-12 shadow-2xl">
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl transform translate-x-20 -translate-y-20" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-400/20 rounded-full blur-2xl transform -translate-x-16 translate-y-16" />
                  <div className="relative z-10 text-white space-y-6">
                    <div className="flex items-center gap-4">
                      <h3 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold`}>The Vision</h3>
                    </div>
                    <p className={`${isMobile ? 'text-sm' : 'text-lg'} text-purple-100 leading-relaxed`}>
                      A world where every researcher has access to AI-powered tools that amplify
                      their creativity, accelerate their discoveries, and connect them with a
                      global community of fellow innovators.
                    </p>
                    <div className="bg-white/10 rounded-2xl p-6 border border-white/20 backdrop-blur-sm">
                      <p className="text-purple-100 italic font-medium">
                        "Research is too important to be slowed down by outdated tools."
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Values Section */}
      <motion.div
        ref={valuesRef}
        className={`relative z-10 landing-section ${isMobile ? 'px-4 py-16' : 'px-8 md:px-16 xl:px-32 2xl:px-40 py-24'}`}
      >
         <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={valuesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className={`text-center ${isMobile ? 'mb-12' : 'mb-20'}`}
          >
            <h2 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-3xl' : 'text-5xl'}`}>
              Our Core Values
            </h2>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 max-w-2xl mx-auto`}>
              The principles that guide everything we build
            </p>
          </motion.div>

          <div className="space-y-8 md:space-y-12">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={valuesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.15, duration: 0.8 }}
                className={`flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} ${isMobile ? 'flex-col' : ''} items-center gap-8 md:gap-16`}
              >
                <div className={`flex-1 ${isMobile ? 'order-1' : ''} space-y-4`}>
                  <div className="flex items-center gap-4">
                    <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-900 dark:text-white`}>
                      {value.title}
                    </h3>
                  </div>
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed max-w-2xl`}>
                    {value.description}
                  </p>
                </div>
                <div className={`flex-1 ${isMobile ? 'order-2 w-full' : ''} relative`}>
                  <div className={`w-full h-64 md:h-80 rounded-3xl bg-gradient-to-br ${index % 4 === 0 ? 'from-purple-400/20 to-blue-400/20' : index % 4 === 1 ? 'from-blue-400/20 to-cyan-400/20' : index % 4 === 2 ? 'from-cyan-400/20 to-purple-400/20' : 'from-pink-400/20 to-purple-400/20'} backdrop-blur-sm border border-white/20 dark:border-white/10 shadow-xl flex items-center justify-center overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10 rounded-3xl pointer-events-none" />
                    <value.Animation />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Story Section */}
      <motion.div
        ref={storyRef}
        className={`relative z-10 landing-section ${isMobile ? 'px-4 py-16' : 'px-8 md:px-16 xl:px-32 2xl:px-40 py-24'}`}
      >
         <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={storyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 via-transparent to-blue-600/10 rounded-3xl blur-3xl" />
            <div className={`relative space-y-8 md:space-y-12`}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={storyInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="text-center"
              >
                <h2 className={`${isMobile ? 'text-3xl' : 'text-5xl'} font-bold text-slate-900 dark:text-white mb-6`}>
                  How Synkro Started
                </h2>
                <p className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300`}>
                  The journey that led us here
                </p>
              </motion.div>

              <div className={`${isMobile ? 'space-y-8' : 'grid grid-cols-2 gap-16'}`}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={storyInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="space-y-6"
                >
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed`}>
                    Synkro was born from the frustration of researchers who were spending more time
                    managing their research tools than actually researching. We watched brilliant minds
                    waste countless hours switching between disconnected applications, manually organizing
                    files, and struggling to extract insights from vast amounts of information.
                  </p>
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed`}>
                    The breaking point came when we realized that while AI was advancing rapidly in
                    other fields, research workflows remained largely unchanged from decades ago.
                    We knew there had to be a better way.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={storyInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="space-y-6"
                >
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed`}>
                    Our founding team combined expertise in artificial intelligence, user experience
                    design, and distributed systems to create something entirely new: a research
                    platform that truly understands how researchers think and work.
                  </p>
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed`}>
                    Today, Synkro serves researchers across academia, industry, and independent research
                    organizations, helping them accelerate discoveries that benefit humanity.
                  </p>
                </motion.div>
              </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={storyInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="text-center"
                >
                <div className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/50 dark:to-blue-950/50 rounded-full border border-purple-200/50 dark:border-purple-700/50">
                  <span className="text-lg font-semibold text-slate-900 dark:text-white">
                    Innovation Through Collaboration
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll to Top Button */}
    </div>
  );
};

export default About; 